import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Menu, X, Phone } from 'lucide-react';

const Header = () => {
  const { theme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'About', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Process', href: '#process' },
    { label: 'Why Us', href: '#why-us' },
    { label: 'Projects', href: '#projects' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <>
      <header 
        className={`
          fixed top-0 left-0 right-0 z-40 transition-all duration-300
          ${isScrolled ? `${theme.colors.navBg} backdrop-blur-md shadow-lg` : 'bg-white/80'}
        `}
        data-testid="header"
      >
        <div className={theme.layout.container}>
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a 
              href="#hero" 
              onClick={(e) => { e.preventDefault(); scrollToSection('#hero'); }}
              className="flex items-center gap-3"
              data-testid="logo"
            >
              {/* Text Logo */}
              <div className="flex items-center">
                <span className="bg-[#ff5218] text-white font-oswald font-black text-lg px-2 py-1">Sydney</span>
                <span className="font-oswald font-bold text-lg text-slate-800 ml-1">Roof Painters</span>
              </div>
            </a>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                  className={`
                    ${theme.typography.bodyFont} ${theme.colors.textSecondary}
                    hover:text-[#ff5218] transition-colors text-sm font-medium
                    uppercase tracking-wider
                  `}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* CTA & Mobile Menu */}
            <div className="flex items-center gap-4">
              <a 
                href="tel:1300304599" 
                className="hidden md:flex items-center gap-2 font-bold text-[#ff5218]"
                data-testid="phone-link"
              >
                <Phone className="w-5 h-5" />
                <span>1300 304 599</span>
              </a>
              
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden p-2 ${theme.colors.textPrimary}`}
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div 
        className={`
          fixed inset-0 z-30 pt-[80px] transition-transform duration-300
          ${theme.colors.background}
          ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
        data-testid="mobile-menu"
      >
        <nav className="flex flex-col items-center gap-6 p-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
              className={`
                ${theme.typography.headingFont} ${theme.colors.textPrimary}
                text-2xl ${theme.typography.headingWeight} uppercase
              `}
            >
              {link.label}
            </a>
          ))}
          <a 
            href="tel:1300304599" 
            className={`mt-4 flex items-center gap-2 ${theme.components.buttonPrimary}`}
          >
            <Phone className="w-5 h-5" />
            <span>1300 304 599</span>
          </a>
        </nav>
      </div>
    </>
  );
};

export default Header;
